import {
  PutObjectCommand,
  PutObjectCommandInput,
  S3Client,
  S3ClientConfig,
} from '@aws-sdk/client-s3';
import { getSignedUrl } from '@aws-sdk/s3-request-presigner';
import { env } from '../../env';
import { ApiError } from '../api-error';
import { HttpStatusCode } from '../http-status';

/**
 * R2 Signed/Presigned URLs Utilities
 *
 * Signed URLs are temporary URLs that grant access to private cloud storage objects
 * without requiring authentication credentials. They contain cryptographic signatures
 * that prove the request is authorized and typically expire after a set time (1 hour here).
 * This allows secure direct uploads/downloads between clients and R2 storage.
 */

// Reuse the same query param type as S3 for consistency
export type R2SignQueryParam = {
  objectName: string;
  contentType: string;
  bucket: string;
  directory?: string;
  region?: string; // Will be ignored for R2, but kept for compatibility
  prefix?: string;
};

/**
 * Direct upload endpoint for Cloudflare R2 storage.
 * Used for server-side file uploads and API operations that require direct R2 access.
 */
export const R2_UPLOAD_PATH = 'https://d6830378c79b481b239149a9c5d10168.r2.cloudflarestorage.com';

/** Default R2 client configuration with credentials and endpoint */
export const defaultOptionsR2: S3ClientConfig = {
  credentials: {
    accessKeyId: env.R2_ACCESS_KEY_ID,
    secretAccessKey: env.R2_SECRET_ACCESS_KEY,
  },
  region: 'auto', // R2 always uses 'auto' as region
  endpoint: R2_UPLOAD_PATH,
  forcePathStyle: true, // Critical for R2: forces bucket name in path, not subdomain
};

/**
 * Generates a signed PUT URL for uploading files to R2
 */
export const getR2SignedPutUrl = async (
  params: PutObjectCommandInput,
  options: Partial<S3ClientConfig> = {},
): Promise<string> => {
  const command = new PutObjectCommand(params);
  return await getR2PresignedUrl(command, options);
};

/**
 * Creates a presigned URL for any R2 command with 1-hour expiration
 */
export const getR2PresignedUrl = async (
  command: any,
  options: Partial<S3ClientConfig> = {},
): Promise<string> => {
  // @ts-ignore
  if (!defaultOptionsR2.credentials.accessKeyId || !defaultOptionsR2.credentials.secretAccessKey) {
    throw Error('R2 credentials are missing');
  }

  // For R2, we always override region to 'auto' and set the endpoint
  const r2Options: S3ClientConfig = {
    ...defaultOptionsR2,
    ...options,
    region: 'auto',
    endpoint: defaultOptionsR2.endpoint,
  };

  const client = new S3Client(r2Options);
  return await getSignedUrl(client, command, { expiresIn: 3600 });
};

/**
 * Constructs the object key path from directory, prefix, and object name
 */
export const getR2ObjectKey = (reqQuery: R2SignQueryParam): string => {
  const filename = reqQuery.prefix
    ? `${reqQuery.prefix}${reqQuery.objectName}`
    : `${reqQuery.objectName}`;

  return reqQuery.directory ? `${reqQuery.directory}/${filename}` : `${filename}`;
};

/**
 * Creates a PUT command for R2 upload with metadata and cache settings
 */
export const getR2PutCommand = (reqQuery: R2SignQueryParam): PutObjectCommandInput => {
  const params: PutObjectCommandInput = {
    Bucket: reqQuery.bucket,
    Key: getR2ObjectKey(reqQuery),
    ContentType: reqQuery.contentType,
    CacheControl: 'max-age=31536000',
    // Note: R2 doesn't use ACL like S3, it handles permissions differently

    Metadata: {
      'uploaded-by': 'bridebook-app',
      'upload-timestamp': new Date().toISOString(),
      ...(reqQuery.directory && { 'original-directory': reqQuery.directory }),
      ...(reqQuery.prefix && { 'original-prefix': reqQuery.prefix }),
    },
  };

  return params;
};

/**
 * Validates required parameters for R2 operations
 */
export const checkRequiredR2QueryParams = (
  bucket: string,
  objectName: string,
  contentType: string,
): void => {
  if (!bucket) {
    throw new ApiError(HttpStatusCode.BadRequest, 'Bucket is missing!');
  }

  if (!objectName) {
    throw new ApiError(HttpStatusCode.BadRequest, 'Object name is missing!');
  }

  if (!contentType) {
    throw new ApiError(HttpStatusCode.BadRequest, 'Content type is missing!');
  }
};
